import { useState, useEffect } from 'react';
import { Header } from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, Activity, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useAuditLog } from '@/hooks/useAuditLog';
import { useSecurityAlerts } from '@/hooks/useSecurityAlerts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function SecurityDashboard() {
  const { getAuditLogs, getTableAuditLogs } = useAuditLog();
  const { alerts, dismissAlert, clearAllAlerts, criticalAlerts, highAlerts } = useSecurityAlerts();
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    document.title = "Segurança | Radar Financeiro";
    loadAuditLogs();
  }, []);
  
  const loadAuditLogs = async () => {
    setLoading(true);
    const logs = await getAuditLogs(20);
    setAuditLogs(logs);
    setLoading(false);
  };
  
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };
  
  const getActionBadge = (action: string) => {
    if (action.includes('DELETE')) return <Badge variant="destructive">DELETE</Badge>;
    if (action.includes('UPDATE')) return <Badge variant="default">UPDATE</Badge>;
    if (action.includes('INSERT')) return <Badge variant="secondary">INSERT</Badge>;
    return <Badge>{action}</Badge>;
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="page-bleed py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold">Dashboard de Segurança</h1>
          </div>
        </div>
        
        {/* Estatísticas de Segurança */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Alertas Críticos
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{criticalAlerts.length}</div>
              <p className="text-xs text-muted-foreground">
                Requerem atenção imediata
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Alertas Altos
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{highAlerts.length}</div>
              <p className="text-xs text-muted-foreground">
                Revisar em breve
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Atividade Recente
              </CardTitle>
              <Activity className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{auditLogs.length}</div>
              <p className="text-xs text-muted-foreground">
                Últimas 20 ações
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Status Geral
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {criticalAlerts.length === 0 ? 'Seguro' : 'Atenção'}
              </div>
              <p className="text-xs text-muted-foreground">
                {criticalAlerts.length === 0 ? 'Nenhum problema detectado' : 'Verifique alertas'}
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Alertas de Segurança */}
        {alerts.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Alertas de Segurança</CardTitle>
                  <CardDescription>Ações suspeitas ou que requerem atenção</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={clearAllAlerts}>
                  Limpar Todos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.map(alert => (
                  <div key={alert.id} className="flex items-start justify-between p-4 border rounded-lg">
                    <div className="flex items-start gap-3 flex-1">
                      <AlertTriangle className="h-5 w-5 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={getSeverityColor(alert.severity)}>
                            {alert.severity.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(alert.timestamp, "PPp", { locale: ptBR })}
                          </span>
                        </div>
                        <p className="text-sm">{alert.message}</p>
                        {alert.metadata && (
                          <pre className="text-xs text-muted-foreground mt-2 bg-muted p-2 rounded">
                            {JSON.stringify(alert.metadata, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissAlert(alert.id)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Log de Auditoria */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Log de Auditoria</CardTitle>
                <CardDescription>Histórico das últimas 20 ações realizadas</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadAuditLogs}>
                <Activity className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando logs...
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma ação registrada ainda
              </div>
            ) : (
              <div className="space-y-2">
                {auditLogs.map(log => (
                  <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4 flex-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {getActionBadge(log.action)}
                          <span className="font-medium text-sm">{log.table_name}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(log.created_at), "PPpp", { locale: ptBR })}
                        </p>
                      </div>
                    </div>
                    {log.record_id && (
                      <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        {log.record_id.slice(0, 8)}...
                      </code>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Recomendações de Segurança */}
        <Card>
          <CardHeader>
            <CardTitle>Recomendações de Segurança</CardTitle>
            <CardDescription>Boas práticas para manter seus dados seguros</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Criptografia ativada</p>
                  <p className="text-xs text-muted-foreground">
                    Seus dados locais estão protegidos com criptografia AES-256
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Rate Limiting ativo</p>
                  <p className="text-xs text-muted-foreground">
                    Proteção contra ataques de força bruta implementada
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Auditoria completa</p>
                  <p className="text-xs text-muted-foreground">
                    Todas as ações críticas são registradas para rastreabilidade
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Revise senhas regularmente</p>
                  <p className="text-xs text-muted-foreground">
                    Recomendamos alterar sua senha a cada 90 dias
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
